import { useEffect } from 'react';

import { useTheme } from '../../hooks/useTheme';

import { NAV_THEMES } from './nav-tokens';
import type { NavCollapseState } from './navCollapse';
import NavPanelAccount from './NavPanelAccount';
import NavPanelBrand from './NavPanelBrand';
import NavPanelEntries from './NavPanelEntries';
import NavPanelFooter from './NavPanelFooter';
import NavPanelSections from './NavPanelSections';
import { COLLAPSED_ONLY, EXPANDED_ONLY, PANEL_WIDTH, panelVariant } from './navRowClass';
import NavSectionFlyout from './NavSectionFlyout';
import type { AppModeInfo } from './use-app-mode';
import type { NavModel, NavModelSection } from './use-nav-model';
import { useNavFlyout } from './useNavFlyout';

interface NebariNavPanelProps {
  nav: NavModel;
  viewer: AppModeInfo;
  /** The viewer's choice, or null to follow the breakpoint default. */
  collapse: NavCollapseState;
  onSetCollapse: (next: NavCollapseState) => void;
}

const FLYOUT_ID = 'nav-section-flyout';

/**
 * The left nav panel, and the whole of the navigation above mobile.
 *
 * Both bands carry the same destinations — every top-level entry — and differ
 * only in whether the labels show. A section's pages are in neither band; they
 * open in a fly-out beside it. Both render and CSS picks, so the first paint is
 * already correct with no viewport hook.
 *
 * The labelled band is the default: a viewer who has never touched the control
 * should be able to read where everything is. Collapsing to the icon rail is
 * opted into, and that choice then holds at every width. The panel always
 * pushes the page across — below `navtab` there is no panel at all, because
 * that width gets the hamburger and the drawer instead.
 *
 * The brand header and the footer are pinned; only the destinations scroll.
 */
export default function NebariNavPanel({
  nav,
  viewer,
  collapse,
  onSetCollapse,
}: NebariNavPanelProps) {
  const { theme } = useTheme();
  const t = NAV_THEMES[theme];
  const variant = panelVariant(collapse);
  const flyout = useNavFlyout();
  const openSection =
    nav.entries.find(
      (e): e is NavModelSection => e.kind === 'section' && e.section.id === flyout.open?.id,
    ) ?? null;
  const closeFlyout = flyout.close;

  // Collapsing or expanding hides the band the open row is in, so the row the
  // fly-out is anchored to stops being on screen.
  useEffect(() => {
    closeFlyout();
  }, [variant, closeFlyout]);

  return (
    <>
      <aside
        // Named for what it contains: every section, not the one you are in.
        // It was the active section's page list until the rail changed hands.
        aria-label="Navigation"
        className={`hidden shrink-0 flex-col py-3.5 navtab:flex ${PANEL_WIDTH[variant]}`}
        style={{ background: t.bg, borderRight: `1px solid ${t.border}` }}
      >
        <NavPanelBrand variant={variant} onSetCollapse={onSetCollapse} />
        <div
          data-testid="nav-panel-icons"
          className={`min-h-0 flex-1 flex-col gap-1 overflow-y-auto ${COLLAPSED_ONLY[variant]}`}
        >
          <NavPanelEntries nav={nav} flyout={flyout} flyoutId={FLYOUT_ID} collapsed />
        </div>
        <div
          data-testid="nav-panel-labelled"
          className={`min-h-0 flex-1 flex-col overflow-y-auto ${EXPANDED_ONLY[variant]}`}
        >
          <NavPanelSections nav={nav} flyout={flyout} flyoutId={FLYOUT_ID} />
          <div className="flex-1" />
          <NavPanelAccount viewer={viewer} />
        </div>
        <NavPanelFooter variant={variant} onSetCollapse={onSetCollapse} />
      </aside>
      {openSection && flyout.open && (
        <NavSectionFlyout
          id={FLYOUT_ID}
          section={openSection.section}
          items={openSection.items}
          anchor={flyout.open.anchor}
          autoFocus={flyout.open.autoFocus}
          onClose={closeFlyout}
          onPointerEnter={flyout.keepOpen}
          onPointerLeave={flyout.scheduleClose}
          onSurface={flyout.setSurface}
        />
      )}
    </>
  );
}
